import { inject, Injectable, OnDestroy, Signal, signal, WritableSignal } from '@angular/core';
import {
  RoleClient,
  SearchRolesRequest,
  SearchRolesResponseItem,
  SortDirection,
} from '../../../core/openapi/identity';
import { BehaviorSubject, map, Observable, of, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { prepareRequest } from '../../../shared/utils/prepare-request.function';
import { LoadingState } from '../../../shared/types/loading.type';
import { outputError } from '../../../shared/utils/output-error.function';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../../shared/const/pagination.constants';

@Injectable()
export class RolesService implements OnDestroy {
  private readonly roleClient = inject(RoleClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private readonly _rolesLoadingState: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.LOADING,
  );

  public readonly rolesLoadingState: Signal<LoadingState> = this._rolesLoadingState.asReadonly();
  public readonly totalRolesCount: WritableSignal<number> = signal<number>(0);
  public readonly totalRolesCountWithoutFilter: WritableSignal<number> = signal<number>(0);

  private readonly rolesRequest$ = new BehaviorSubject<SearchRolesRequest>({
    skip: 0,
    take: DEFAULT_TABLE_PAGE_SIZE,
    includeTotalCount: true,
    sortExpressions: [{ propertyName: 'name', direction: SortDirection.NUMBER_1 }],
  });

  public readonly roles$: Observable<SearchRolesResponseItem[]> = this.rolesRequest$.pipe(
    tap(() => {
      this._rolesLoadingState.set(LoadingState.LOADING);
    }),
    switchMap((request) =>
      this.roleClient.roleSearchPost(request).pipe(
        catchError((err) => {
          this.toastService.error({
            message: this.translateService.instant('monitoring.roles.toast.roles-fetching-failed', {
              error: outputError(err, this.translateService.instant('errors.unknown')),
            }),
          });

          return of({ items: [], totalCount: 0 });
        }),
      ),
    ),
    tap((res) => {
      this.totalRolesCount.set(res.totalCount ?? 0);

      if (!this.rolesRequest$.value.keyword) {
        this.totalRolesCountWithoutFilter.set(res.totalCount ?? 0);
      }
    }),
    map(({ items }): SearchRolesResponseItem[] => items ?? []),
    tap(() => {
      this._rolesLoadingState.set(LoadingState.READY);
    }),
  );

  public triggerRolesUpdate(params: Partial<SearchRolesRequest>): void {
    this.rolesRequest$.next({
      ...this.rolesRequest$.value,
      ...prepareRequest(params),
    });
  }

  public ngOnDestroy(): void {
    this.rolesRequest$.complete();
  }
}
